import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Alert,
  CodeEditor,
  Flex,
  FlexColumn,
  FormControls,
  InfoTooltip,
  Switch,
  TextField,
  GenerateAIButton,
} from '@elementary/common-ui/components';
import useAccount from '@elementary/common-ui/hooks/useAccount';
import { Divider, Stack, Tooltip, Typography } from '@mui/material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { TEST_TEMPLATE } from '@elementary/common-ui/constants/queryKeys';
import Ajv from 'ajv';
import YAML, { YAMLException } from 'js-yaml';
import { Controller, UseFormReturn } from 'react-hook-form';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { AddTestForm } from 'shared/types';
import * as api from 'testManagement/services/api';
import SeveritySelector from '../SeveritySelector';
import WhereClauseControl from '../WhereClauseControl';
import TableTestDescriptionField from './shared/TableTestDescriptionField';
import TestMetadataForm from './shared/TestMetadataForm';

const AiContainer = styled(Stack)`
  display: none; // remove to restore the ai feature
  flex-direction: row-reverse;
  gap: 16px;
  width: 100%;
`;

const ajv = new Ajv({ allErrors: true });

type YamlValidationError = {
  line: number;
  message: string;
};

const getSchema = (
  test_name: string | undefined,
  schema: object | undefined,
) => {
  if (!schema || !test_name) {
    return undefined;
  }
  return {
    type: 'array',
    items: {
      type: 'object',
      properties: {
        [test_name]: schema,
      },
      required: [test_name],
      additionalProperties: false,
    },
    minItems: 1,
    maxItems: 1,
  };
};

const removeLineComments = (yaml: string) => {
  const lines = yaml.split('\n');
  const uncommentedLines = lines.filter((line) => !line.trim().startsWith('#'));
  return uncommentedLines.join('\n');
};

const findParamLine = (lines: string[], paramPath: string[]) => {
  let index = 0;
  paramPath.forEach((param) => {
    const relativeIndex =
      lines.slice(index + 1).findIndex((l) => l.includes(`${param}:`)) + 1;
    if (relativeIndex !== -1) {
      index += relativeIndex;
    }
  });
  return index;
};

const jsonSchemaErrorsToValidationErrors = (yaml: string, errors: any[]) => {
  if (!errors) {
    return [];
  }
  const lines = yaml.split('\n');
  return errors.map((error) => {
    const pathParts = error.instancePath.split('/').slice(1);
    const paramName = pathParts[pathParts.length - 1];
    const paramStr = paramName ? `Parameter "${paramName}"` : 'Configuration';
    const line = findParamLine(lines, pathParts);
    let { message } = error;
    if (error.params?.allowedValues) {
      message = `${message} (${error.params.allowedValues.join(', ')})`;
    }
    if (error.params?.additionalProperty) {
      message = `${message} (${error.params.additionalProperty})`;
    }
    return {
      line,
      message: `${paramStr}: ${message}`,
    };
  });
};

const validateYaml = (
  value: string,
  schema: object | undefined,
): YamlValidationError[] => {
  try {
    const parsed = YAML.load(value);
    if (!value || !schema) {
      return [];
    }
    const validate = ajv.compile(schema);
    validate(parsed);
    return jsonSchemaErrorsToValidationErrors(value, validate.errors || []);
  } catch (e) {
    if (!(e instanceof YAMLException)) {
      return [];
    }
    return [
      {
        line: e.mark.line,
        message: `Invalid YAML: ${e.reason}`,
      },
    ];
  }
};

const ErrorsContainer = ({ errors }: { errors: string[] }) => (
  <Alert severity="error">
    {errors.map((error, index) => (
      <Typography key={index}>{error}</Typography>
    ))}
  </Alert>
);

type Props = {
  methods: UseFormReturn<AddTestForm, any, undefined>;
  appliesTo: 'column' | 'table';
  editMode?: boolean;
};

const YamlTest = ({ methods, appliesTo, editMode }: Props) => {
  const { control, setValue, register, watch, getValues } = methods;

  const [rawYaml, setRawYaml] = useState<string>(editMode ? watch('yaml') : '');
  const [isConfigExpanded, setIsConfigExpanded] = useState(false);
  const [showTextbox, setShowTextbox] = useState(false);
  const [userInput, setUserInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const yamlTemplateName = watch('yamlTemplateName');
  const selectedEnvId = useSelectedEnv();
  const { data: account } = useAccount();

  const { data: selectedTemplate } = useQuery(
    [TEST_TEMPLATE, yamlTemplateName, appliesTo],
    () => api.getTestTemplateData(yamlTemplateName!, selectedEnvId!),
    { enabled: !editMode },
  );

  useEffect(() => {
    if (!editMode) {
      setRawYaml(selectedTemplate?.config_template_yaml || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate, setRawYaml]);

  const validationErrors = useMemo(
    () =>
      validateYaml(
        rawYaml,
        getSchema(
          selectedTemplate?.test_template.test_name,
          selectedTemplate?.config_schema,
        ),
      ),
    [selectedTemplate, rawYaml],
  );

  useEffect(() => {
    setValue('yaml', removeLineComments(rawYaml));
  }, [rawYaml, setValue]);

  useEffect(() => {
    setValue('yamlValid', validationErrors.length === 0);
  }, [validationErrors, setValue]);

  const allowConditionalSeverity =
    editMode || selectedTemplate?.test_template.supports_conditional_severity;

  useEffect(() => {
    if (editMode) return;
    methods.setValue('severity', { type: 'error' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate]);

  const {
    mutateAsync: generateTestConfiguration,
    isLoading: isGenerateTestConfigurationLoading,
  } = useMutation(async (inputText?: string) =>
    api.generateTestConfiguration(
      selectedEnvId!,
      getValues('resources')[0],
      selectedTemplate?.test_template.test_name!,
      rawYaml,
      appliesTo,
      inputText ? userInput : undefined,
    ),
  );

  const handleTextboxSubmit = async () => {
    setIsSubmitting(true);
    try {
      const aiTestConfig = await generateTestConfiguration(userInput);
      setRawYaml(aiTestConfig.yaml_config);
      setValue('description', aiTestConfig.test_description);
      setShowTextbox(false);
      setUserInput('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGenerateButtonClick = () => {
    if (!showTextbox) {
      setShowTextbox(true);
    }
  };

  const disabledAiTooltip = account?.ai_features_enabled
    ? undefined
    : 'Access to AI features and agents required';

  return (
    <FlexColumn sx={{ gap: 3, p: 3, minHeight: '100%' }}>
      <Flex sx={{ height: '100%' }}>
        <FlexColumn sx={{ flex: 3, gap: 3, padding: 0 }}>
          <Flex sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <FlexColumn sx={{ gap: 0.5 }}>
              <Typography sx={{ fontWeight: 500 }}>
                Test configuration
              </Typography>
              {editMode ? (
                <Typography sx={{ color: 'common.grapy2' }}>
                  Editing this section will be supported soon
                </Typography>
              ) : (
                <Typography sx={{ color: 'common.grapy2' }}>
                  {`Configure your test using the YAML configuration below. The test
                  will be added to your dbt project's test configurations.`}
                </Typography>
              )}
            </FlexColumn>
          </Flex>

          <CodeEditor
            key={`${yamlTemplateName}-${isGenerateTestConfigurationLoading}`}
            readOnly={editMode || isGenerateTestConfigurationLoading}
            grayedOut={editMode || isGenerateTestConfigurationLoading}
            mode="yaml"
            height="100%"
            markers={validationErrors.map((error) => ({
              startRow: error.line,
              startCol: -1,
              endRow: error.line,
              endCol: 10000,
              className: 'error-marker',
              type: 'text',
            }))}
            annotations={validationErrors.map((error) => ({
              row: error.line,
              text: error.message,
              type: 'error',
            }))}
            value={rawYaml}
            onChange={setRawYaml}
          />
          {validationErrors.length > 0 && (
            <ErrorsContainer errors={validationErrors.map((e) => e.message)} />
          )}

          {account?.ai_features_enabled && (
            <AiContainer>
              <Tooltip title={disabledAiTooltip}>
                <div>
                  <GenerateAIButton
                    sx={{ alignSelf: 'flex-end' }}
                    onClick={handleGenerateButtonClick}
                    loading={isGenerateTestConfigurationLoading || isSubmitting}
                    disabled={!!disabledAiTooltip || isSubmitting}
                  >
                    {isGenerateTestConfigurationLoading || isSubmitting
                      ? 'Processing request...'
                      : 'Configure test with AI'}
                  </GenerateAIButton>
                </div>
              </Tooltip>
              {showTextbox && (
                <TextField
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  placeholder="Describe your test or requirements..."
                  disabled={isSubmitting}
                  fullWidth
                  size="large"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleTextboxSubmit();
                    }
                  }}
                />
              )}
            </AiContainer>
          )}
        </FlexColumn>

        <Divider orientation="vertical" flexItem sx={{ mx: 4 }} />

        <FlexColumn sx={{ flex: 2 }}>
          <FormControls.FieldsWrapper sx={{ height: '100%' }}>
            <TableTestDescriptionField methods={methods} showCustomTestName />

            <TestMetadataForm methods={methods} />

            <FormControls.FieldsWrapper>
              <FormControls.TestAccordion
                variant="secondary"
                summary={
                  <Typography sx={{ fontWeight: 500 }}>
                    Advanced configuration
                  </Typography>
                }
                expanded={isConfigExpanded}
                onChange={(_, val) => setIsConfigExpanded(val)}
              >
                <FlexColumn sx={{ pt: '18px', gap: '24px' }}>
                  <Controller
                    name="severity"
                    control={control}
                    render={({ field }) => (
                      <SeveritySelector
                        {...field}
                        allowConditional={allowConditionalSeverity}
                      />
                    )}
                  />

                  {!editMode && (
                    <>
                      <FormControls.FormTitle>Others</FormControls.FormTitle>

                      <FormControls.Row>
                        <FormControls.FormText>Limit:</FormControls.FormText>
                        <TextField
                          sx={{ width: '134px' }}
                          placeholder="Set limit"
                          numeric
                          {...register('limit')}
                        />
                        <InfoTooltip title="Limit the number of failures that will be returned by the test query" />
                      </FormControls.Row>

                      <FormControls.Row>
                        <FormControls.FormText>
                          Store failure data:
                        </FormControls.FormText>
                        <Switch {...register('shouldStoreFailures')} />
                        <InfoTooltip title="Whether or not the test failures are stored in the database" />
                      </FormControls.Row>

                      <Controller
                        name="whereClause"
                        control={control}
                        render={({ field }) => (
                          <WhereClauseControl {...field} />
                        )}
                      />
                    </>
                  )}
                </FlexColumn>
              </FormControls.TestAccordion>
            </FormControls.FieldsWrapper>
          </FormControls.FieldsWrapper>
        </FlexColumn>
      </Flex>
    </FlexColumn>
  );
};

export default YamlTest;
